import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  View,
  FlatList,
  RefreshControl,
  StatusBar,
  StyleSheet,
  TouchableOpacity,
  Text,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { RootStackParamList } from '@/navigation/RootNavigator';
import { useResponsive } from '@/hooks/common/useResponsive';
import { useResidentScreen } from '@/hooks/residents/useResidentScreen';
import { useAppSelector, useAppDispatch } from '@/store';
import { useGetTagsQuery, useGetThreadsQuery } from '@/store/api/corkBoard/corkBoardApi';
import { clearDeletedThreads, clearFeedReplace } from '@/store/slices/residents/corkBoardUISlice';
import { useAccumulatedPaging } from '@/hooks/common/useAccumulatedPaging';
import { useCorkBoardFilters } from '@/hooks/residents/corkBoard/useCorkBoardFilters';
import { ResidentMobileHeaderWithMenu } from '@/components/residents/mobile/ResidentMobileHeaderWithMenu';
import { MobileScreenLayout } from '@/components/residents/mobile/MobileScreenLayout';
import {
  TagFilterBar,
  ThreadCard,
  ThreadCardSkeleton,
  SortDropdown,
  CorkBoardSearchBar,
  CorkBoardEmptyState,
} from '@/components/residents/corkBoard';
import { SEMANTIC_COLORS, COMPONENT_COLORS } from '@/constants/residents/residentColors';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import type { CorkBoardThreadSummary } from '@/types/residents/corkBoard';

type CommunityBoardNavigationProp = StackNavigationProp<RootStackParamList>;

export const CommunityBoardScreen: React.FC = () => {
  const navigation = useNavigation<CommunityBoardNavigationProp>();
  const dispatch = useAppDispatch();
  const { layoutMode } = useResponsive();
  const user = useAppSelector((state) => state.auth.user);
  const propertyId = user?.propertyId;
  const deletedThreadIds = useAppSelector((state) => state.corkBoardUI.deletedThreadIds);
  const feedNeedsReplace = useAppSelector((state) => state.corkBoardUI.feedNeedsReplace);

  const isMobile = layoutMode === 'mobile';
  const isTablet = layoutMode === 'tablet';

  const {
    handleNavigate,
    handleLogoPress,
    handleSignOut,
    handleNotificationPress,
  } = useResidentScreen('CommunityBoard', navigation as any);

  // Filter / sort / search / pagination state
  const filters = useCorkBoardFilters({ propertyId });

  // Fetch tags
  const {
    data: rawTags = [],
    isLoading: isLoadingTags,
  } = useGetTagsQuery(
    { propertyId: propertyId ?? 0 },
    { skip: !propertyId },
  );

  const tags = useMemo(
    () => [...rawTags].sort((a, b) => (a.slug === 'announcements' ? -1 : b.slug === 'announcements' ? 1 : 0)),
    [rawTags],
  );

  // Fetch threads
  const {
    currentData: threadsData,
    isLoading: isLoadingThreads,
    isFetching: isFetchingThreads,
    refetch: refetchThreads,
    isError: isThreadsError,
  } = useGetThreadsQuery(
    filters.queryParams,
    { skip: filters.shouldSkipQuery },
  );

  const threads = threadsData?.threads ?? [];
  const announcements = useMemo(() => threadsData?.announcements ?? [], [threadsData?.announcements]);
  const pagination = threadsData?.pagination;
  const hasMorePages = pagination ? pagination.currentPage < pagination.totalPages - 1 : false;

  // Infinite scroll accumulation
  const { accumulatedRows, clearIfPendingReplaceAndNoRows, markForReplace } = useAccumulatedPaging({
    rows: threads,
    accumulationKey: filters.accumulationKey,
  });

  // Clear stale accumulation when loading finishes with no results
  React.useEffect(() => {
    clearIfPendingReplaceAndNoRows(isLoadingThreads);
  }, [isLoadingThreads, clearIfPendingReplaceAndNoRows]);

  useEffect(() => {
    if (feedNeedsReplace) {
      markForReplace();
      dispatch(clearFeedReplace());
      if (filters.page !== 0) {
        filters.resetPage();
      }
    }
  }, [feedNeedsReplace, markForReplace, dispatch, filters]);

  const visibleRows = useMemo(
    () => deletedThreadIds.length > 0
      ? accumulatedRows.filter((t) => !deletedThreadIds.includes(String(t.id)))
      : accumulatedRows,
    [accumulatedRows, deletedThreadIds],
  );

  const isInitialLoad = isLoadingThreads && visibleRows.length === 0;
  const isLoadingMore = isFetchingThreads && filters.page > 0;

  const [isManualRefreshing, setIsManualRefreshing] = useState(false);

  useEffect(() => {
    if (!isFetchingThreads) {
      setIsManualRefreshing(false);
    }
  }, [isFetchingThreads]);

  // Navigation handlers
  const handleThreadPress = useCallback((thread: CorkBoardThreadSummary) => {
    navigation.navigate('ThreadDetail', { threadId: thread.id });
  }, [navigation]);

  const handleTagPressInCard = useCallback((tagId: number) => {
    filters.toggleTag(tagId);
  }, [filters]);

  const handleCreatePost = useCallback(() => {
    navigation.navigate('CreateThread');
  }, [navigation]);

  const handleMyPosts = useCallback(() => {
    navigation.navigate('MyThreads');
  }, [navigation]);

  const handleSavedPosts = useCallback(() => {
    navigation.navigate('SavedThreads');
  }, [navigation]);

  const handleRefresh = useCallback(() => {
    setIsManualRefreshing(true);
    dispatch(clearDeletedThreads());
    markForReplace();
    if (filters.page === 0) {
      refetchThreads();
    } else {
      filters.resetPage();
    }
  }, [filters, refetchThreads, markForReplace, dispatch]);

  const handleEndReached = useCallback(() => {
    if (!isFetchingThreads && hasMorePages) {
      filters.loadNextPage();
    }
  }, [isFetchingThreads, hasMorePages, filters]);

  // Render items
  const renderThread = useCallback(({ item }: { item: CorkBoardThreadSummary }) => (
    <ThreadCard
      thread={item}
      onPress={handleThreadPress}
      onTagPress={handleTagPressInCard}
    />
  ), [handleThreadPress, handleTagPressInCard]);

  const keyExtractor = useCallback((item: CorkBoardThreadSummary) => item.id, []);

  // List header: tag filter, search, sort, pinned announcements
  const ListHeaderComponent = useMemo(() => (
    <View style={styles.headerSection}>
      {/* Quick Links: My Posts + Saved */}
      <View style={styles.quickLinksRow}>
        <TouchableOpacity onPress={handleMyPosts} style={styles.quickLink} activeOpacity={0.7} accessibilityRole="button" accessibilityLabel="My Posts" accessibilityHint="View your created posts">
          <RESIDENTS_SVG_COMPONENTS.USER width={14} height={14} color={SEMANTIC_COLORS.interactive.primary} />
          <Text style={styles.quickLinkText}>My Posts</Text>
        </TouchableOpacity>
        <TouchableOpacity onPress={handleSavedPosts} style={styles.quickLink} activeOpacity={0.7} accessibilityRole="button" accessibilityLabel="Saved" accessibilityHint="View your saved posts">
          <RESIDENTS_SVG_COMPONENTS.STAR width={14} height={14} color={SEMANTIC_COLORS.interactive.primary} />
          <Text style={styles.quickLinkText}>Saved</Text>
        </TouchableOpacity>
      </View>

      {/* Tag Filters */}
      {!isLoadingTags && tags.length > 0 && (
        <TagFilterBar
          tags={tags}
          selectedTagIds={filters.selectedTagIds}
          onToggleTag={filters.toggleTag}
          onClearFilters={filters.clearTags}
        />
      )}

      {/* Search + Sort row */}
      <View style={[styles.controlsContainer, !isMobile && styles.controlsContainerRow]}>
        <View style={!isMobile ? styles.searchWrapperFlex : undefined}>
          <CorkBoardSearchBar
            value={filters.searchInput}
            onChangeText={filters.setSearch}
            onClear={filters.clearSearch}
          />
        </View>
        <View style={styles.sortWrapper}>
          <SortDropdown value={filters.sortBy} onChange={filters.setSortBy} />
        </View>
      </View>

      {/* Pinned Announcements */}
      {announcements.length > 0 && (
        <View style={styles.announcementsSection}>
          {announcements.map((thread) => (
            <ThreadCard
              key={thread.id}
              thread={thread}
              onPress={handleThreadPress}
              onTagPress={handleTagPressInCard}
            />
          ))}
        </View>
      )}
    </View>
  ), [
    isMobile,
    handleMyPosts, handleSavedPosts,
    isLoadingTags, tags, filters.selectedTagIds, filters.toggleTag, filters.clearTags,
    filters.searchInput, filters.setSearch, filters.clearSearch,
    filters.sortBy, filters.setSortBy,
    announcements, handleThreadPress, handleTagPressInCard,
  ]);

  // Empty / error / skeleton states
  const ListEmptyComponent = useMemo(() => {
    if (isInitialLoad || (isFetchingThreads && visibleRows.length === 0)) return <ThreadCardSkeleton />;
    if (isThreadsError) return <CorkBoardEmptyState variant="error" onRetry={handleRefresh} />;
    if (filters.hasActiveFilters) return <CorkBoardEmptyState variant="noResults" />;
    if (announcements.length > 0) return null;
    return <CorkBoardEmptyState variant="noThreads" onCreatePost={handleCreatePost} />;
  }, [isInitialLoad, isFetchingThreads, visibleRows.length, isThreadsError, filters.hasActiveFilters, announcements.length, handleRefresh, handleCreatePost]);

  const ListFooterComponent = useMemo(() => {
    if (!isLoadingMore) return null;
    return (
      <View style={styles.footerLoader}>
        <ActivityIndicator size="small" color={SEMANTIC_COLORS.interactive.primary} />
      </View>
    );
  }, [isLoadingMore]);

  // Feed content (shared between mobile/tablet/desktop)
  const renderFeed = () => (
    <FlatList
      data={visibleRows}
      renderItem={renderThread}
      keyExtractor={keyExtractor}
      ListHeaderComponent={ListHeaderComponent}
      ListEmptyComponent={ListEmptyComponent}
      ListFooterComponent={ListFooterComponent}
      contentContainerStyle={styles.feedContentContainer}
      ItemSeparatorComponent={ItemSeparator}
      onEndReached={handleEndReached}
      onEndReachedThreshold={0.4}
      showsVerticalScrollIndicator={false}
      removeClippedSubviews={Platform.OS !== 'web'}
      maxToRenderPerBatch={10}
      windowSize={5}
      initialNumToRender={10}
      updateCellsBatchingPeriod={50}
      refreshControl={
        <RefreshControl
          refreshing={isManualRefreshing}
          onRefresh={handleRefresh}
          tintColor={SEMANTIC_COLORS.text.primary}
        />
      }
    />
  );

  // Mobile and Tablet layout
  if (isMobile || isTablet) {
    return (
      <View style={styles.screenContainer}>
        <StatusBar barStyle="light-content" />
        <ResidentMobileHeaderWithMenu
          onLogoPress={handleLogoPress}
          onSignOut={handleSignOut}
          onNavigate={handleNavigate}
          onNotificationPress={handleNotificationPress}
          currentPath="/newsfeed"
          currentTitle="Newsfeed"
          showNotification
        />
        <MobileScreenLayout
          scrollable={false}
          lightStatusBar={false}
        >
          <View style={styles.feedContainer}>
            {renderFeed()}
            <TouchableOpacity
              style={styles.fab}
              onPress={handleCreatePost}
              activeOpacity={0.85}
              accessibilityRole="button"
              accessibilityLabel="Create new post"
            >
              <RESIDENTS_SVG_COMPONENTS.CIRCLE_PLUS width={26} height={26} color={COMPONENT_COLORS.button.primaryText} />
            </TouchableOpacity>
          </View>
        </MobileScreenLayout>
      </View>
    );
  }

  // Desktop layout
  return (
    <View style={styles.screenContainer}>
      <View style={styles.desktopWrapper}>
        {renderFeed()}
        <TouchableOpacity
          style={styles.fab}
          onPress={handleCreatePost}
          activeOpacity={0.85}
          accessibilityRole="button"
          accessibilityLabel="Create new post"
        >
          <RESIDENTS_SVG_COMPONENTS.CIRCLE_PLUS width={26} height={26} color="#FFFFFF" />
        </TouchableOpacity>
      </View>
    </View>
  );
};

const ItemSeparator = () => <View style={styles.separator} />;

const styles = StyleSheet.create({
  screenContainer: {
    flex: 1,
    backgroundColor: Platform.OS !== 'web' ? SEMANTIC_COLORS.background.secondary : SEMANTIC_COLORS.background.primary,
  },
  feedContainer: {
    flex: 1,
  },
  desktopWrapper: {
    flex: 1,
    maxWidth: 960,
    alignSelf: 'center',
    width: '100%',
    paddingHorizontal: 40,
  },
  feedContentContainer: {
    paddingBottom: 80,
  },
  headerSection: {
    gap: 12,
    paddingTop: 12,
    paddingBottom: 8,
  },
  quickLinksRow: {
    flexDirection: 'row',
    gap: 10,
    paddingHorizontal: 16,
  },
  quickLink: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: COMPONENT_COLORS.card.background,
    borderWidth: 1,
    borderColor: COMPONENT_COLORS.card.border,
    borderRadius: 10,
    paddingVertical: 8,
    paddingHorizontal: 14,
  },
  quickLinkText: {
    fontSize: 13,
    fontWeight: '500',
    color: SEMANTIC_COLORS.interactive.primary,
  },
  controlsContainer: {
    paddingHorizontal: 16,
    gap: 10,
  },
  controlsContainerRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  searchWrapperFlex: {
    flex: 1,
  },
  sortWrapper: {
    flexDirection: 'row',
  },
  announcementsSection: {
    gap: 10,
    paddingTop: 4,
  },
  separator: {
    height: 10,
  },
  footerLoader: {
    paddingVertical: 20,
    alignItems: 'center',
  },
  fab: {
    position: 'absolute',
    bottom: 20,
    right: 20,
    width: 52,
    height: 52,
    borderRadius: 26,
    backgroundColor: COMPONENT_COLORS.button.primaryBg,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 6,
    elevation: 8,
  },
});
